import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { AlertTriangle, X, Wrench, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { invokeWithRetry } from "@/lib/invokeWithRetry";
import { isProductionMode, isProductionModeCached, isProductionModeSync } from "@/lib/environment";
import { cn } from "@/lib/utils";
import { logger } from "@/lib/logger";
import { LogLevelControl } from "@/components/dev/LogLevelControl";
import {
  readCachedStatus,
  writeCachedStatus,
  type DevModeStatus,
} from "@/components/dev/devModeBannerCache";

export function DevModeBanner() {
  const { t } = useTranslation();
  // Synchroner Erst-Render aus dem Session-Cache: ist der Status bekannt, gibt es
  // keinen Layout-Shift mehr beim Auflösen des async check-dev-mode-Calls.
  const cachedStatus = readCachedStatus();
  const [status, setStatus] = useState<DevModeStatus | null>(cachedStatus);
  const [isHidden, setIsHidden] = useState(() => {
    return sessionStorage.getItem("devModeBannerHidden") === "true";
  });
  // Wenn der Status bereits gecacht ODER bereits als Produktion bekannt ist, ist
  // der Erst-Render schon final → nicht "loading" (sonst flackert/springt es).
  const [isLoading, setIsLoading] = useState(
    () => cachedStatus === null && !(isProductionModeCached() && isProductionModeSync()),
  );

  useEffect(() => {
    const checkDevMode = async () => {
      try {
        // First check cached production mode
        if (isProductionModeCached() && isProductionModeSync()) {
          // Already know we're in production, no need to fetch
          setIsLoading(false);
          return;
        }

        const { data, error } = await invokeWithRetry<DevModeStatus>("check-dev-mode");

        if (error) {
          logger.error("Error checking dev mode:", error);
          return;
        }

        if (data) {
          writeCachedStatus(data);
        }
        setStatus(data);
      } catch (error) {
        logger.error("Error checking dev mode:", error);
      } finally {
        setIsLoading(false);
      }
    };

    checkDevMode();
  }, []);

  const handleHide = () => {
    setIsHidden(true);
    sessionStorage.setItem("devModeBannerHidden", "true");
  };

  // Don't show anything while loading or if hidden
  if (isLoading || isHidden) {
    return null;
  }

  // Don't show if in production mode or if status is unknown (conservative: hide on error)
  if (!status || status.isProductionMode) {
    return null;
  }

  const hasMissingSecrets = status?.missingSecrets && status.missingSecrets.length > 0;

  return (
    // TES-1090: Banner ist Teil des Layout-Flows (AppLayout: flex-col über der Shell)
    // statt fixed-Overlay — er versetzt TopAppBar/Inhalt um seine eigene Höhe und
    // gibt den Platz beim Schließen (X) automatisch wieder frei. Kein z-Overlay
    // mehr über TopAppBar/Filterleiste.
    <div
      data-testid="dev-mode-banner"
      className={cn(
        "relative shrink-0 w-full px-4 py-2",
        "bg-warning/90 text-warning-foreground backdrop-blur-sm",
        "flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-sm font-medium",
        "shadow-md",
      )}
    >
      <div className="flex items-center gap-2">
        <AlertTriangle className="h-4 w-4 flex-shrink-0" />
        <span>{t("dev.developmentModeActive", "Entwicklungsmodus aktiv")}</span>
      </div>

      {hasMissingSecrets && (
        <div className="flex items-center gap-1.5 px-2 py-0.5 bg-warning-foreground/10 rounded text-xs">
          <AlertCircle className="h-3.5 w-3.5" />
          <span>
            {t("dev.missingSecrets", "Fehlend")}: {status.missingSecrets.join(", ")}
          </span>
        </div>
      )}

      <Link
        to="/admin/dev-tools"
        className="flex items-center gap-1.5 px-2 py-1 bg-warning-foreground/20 hover:bg-warning-foreground/30 rounded transition-colors text-xs"
      >
        <Wrench className="h-3.5 w-3.5" />
        <span>{t("nav.devTools", "Dev-Tools")}</span>
      </Link>

      {/* TES-1636: runtime log-level control — dev build only (banner is already
          dev-mode-gated; the extra DEV guard keeps it out of prod builds). */}
      {import.meta.env.DEV && <LogLevelControl />}

      <Button
        variant="ghost"
        size="icon"
        onClick={handleHide}
        className="absolute right-2 top-1/2 -translate-y-1/2 h-6 w-6 hover:bg-warning-foreground/20"
        aria-label={t("common.hide", "Ausblenden")}
      >
        <X className="h-4 w-4" />
      </Button>
    </div>
  );
}
